import type { TeamPulseActivity, TeamPulseMember, TeamPulseReport } from './api'
import { normalizeMemberName, type PeriodKey } from './team-pulse-view'

export const ACTIVITY_METRICS = [
  { key: 'utterances', label: '会議発言', provider: 'meeting_speaker', metric: 'utterances', unit: '回', color: '#8b5cf6' },
  { key: 'chatwork', label: 'Chatwork', provider: 'chatwork', metric: 'messages', unit: '件', color: '#2563eb' },
  { key: 'slack', label: 'Slack', provider: 'slack', metric: 'messages', unit: '件', color: '#06b6d4' },
  { key: 'github', label: 'GitHub', provider: 'github', metric: 'observations', unit: '件', color: '#475569' },
  { key: 'fieldy', label: 'Fieldy', provider: 'fieldy_slot', metric: 'conversations', unit: '件', color: '#059669' },
] as const
export type MetricKey = typeof ACTIVITY_METRICS[number]['key']
export type MetricSelection = MetricKey | 'all'
export type SortOrder = 'name' | 'asc' | 'desc'
export type SavedPeriod = TeamPulseActivity['periods'][number]
export interface BoardMeasure {
  key: MetricKey
  value: number | null
  savedValue: number | null
  reason: string | null
  overallReason: string | null
}
export interface BoardPerson {
  id: string
  name: string
  narrative: TeamPulseMember | null
  measures: BoardMeasure[]
}

/** Join only mutually unique names. Exact normalized matches take precedence over substrings. */
export function associateNarratives(activity: TeamPulseActivity | null, narratives: TeamPulseMember[]) {
  const matched = new Map<string, TeamPulseMember>()
  const used = new Set<number>()
  const people = activity?.members ?? []
  const names = people.map(p => normalizeMemberName(p.displayName))
  const reportNames = narratives.map(p => normalizeMemberName(p.name))
  const exactCandidates = names.map(name => reportNames.flatMap((other, i) => name && name === other ? [i] : []))
  for (let p = 0; p < people.length; p++) {
    const candidates = exactCandidates[p]
    if (candidates.length !== 1 || exactCandidates.filter(c => c.includes(candidates[0])).length !== 1) continue
    matched.set(people[p].memberId, narratives[candidates[0]])
    used.add(candidates[0])
  }
  const fuzzy = names.map((name, p) => exactCandidates[p].length || !name ? [] : reportNames.flatMap((other, n) =>
    !used.has(n) && !names.includes(other) && other && (name.includes(other) || other.includes(name)) ? [n] : []))
  for (let p = 0; p < people.length; p++) {
    const candidates = fuzzy[p]
    if (candidates.length !== 1 || fuzzy.filter(c => c.includes(candidates[0])).length !== 1) continue
    matched.set(people[p].memberId, narratives[candidates[0]])
    used.add(candidates[0])
  }
  return { matched, unmatched: narratives.filter((_, i) => !used.has(i)) }
}

/** The API's period list is the current calendar; only metric rows prove a saved interval. */
export function latestSavedPeriod(activity: TeamPulseActivity | null, period: PeriodKey): SavedPeriod | null {
  const ranges = activity?.members.flatMap(person => person.metrics.filter(row =>
    row.memberId === person.memberId && row.periodType === period && row.periodStart < row.periodEnd &&
    ACTIVITY_METRICS.some(m => m.provider === row.provider && m.metric === row.metric))) ?? []
  const newest = [...ranges].sort((a, b) => b.periodEnd.localeCompare(a.periodEnd) || b.periodStart.localeCompare(a.periodStart))[0]
  return newest ? { periodType: period, periodStart: newest.periodStart, periodEnd: newest.periodEnd } : null
}

export function buildBoard(activity: TeamPulseActivity | null, report: TeamPulseReport | null, period: PeriodKey) {
  const range = latestSavedPeriod(activity, period)
  const { matched, unmatched } = associateNarratives(activity, report?.members ?? [])
  const people: BoardPerson[] = (activity?.members ?? []).map(person => ({
    id: person.memberId,
    name: person.displayName,
    narrative: matched.get(person.memberId) ?? null,
    measures: ACTIVITY_METRICS.map(def => {
      const rows = person.metrics.filter(row => row.memberId === person.memberId && row.periodType === period && row.provider === def.provider && row.metric === def.metric)
      const sameRange = rows.filter(row => range && row.periodStart === range.periodStart && row.periodEnd === range.periodEnd)
      const hit = sameRange.length === 1 ? sameRange[0] : null
      const savedValue = hit?.value ?? rows[0]?.value ?? null
      const coverage = activity?.coverage.find(c => c.provider === def.provider)
      let reason: string | null = null
      if (coverage?.state === 'absent') reason = '未接続'
      else if (!person.linkedProviders) reason = '人物との紐付け状況未確認'
      else if (coverage?.state === 'unlinked' || (person.linkedProviders && !person.linkedProviders.includes(def.provider))) reason = '人物との紐付け不足'
      else if (!coverage) reason = '連携状況未確認'
      else if (!range) reason = 'この期間は未計測'
      else if (sameRange.length > 1) reason = '同じ期間の値が重複'
      else if (rows.length && !hit) reason = '集計期間が不一致'
      else if (!hit) reason = '未計測（記録の欠落）'
      else if (!Number.isFinite(hit.value) || hit.value < 0) reason = '数値を確認できない'
      const overallReason = null
      return { key: def.key, value: reason ? null : hit?.value ?? null, savedValue, reason, overallReason }
    }),
  }))
  return { people, unmatched, range }
}

export function selectedMeasures(person: BoardPerson, selection: MetricSelection) {
  return person.measures.filter(m => selection === 'all' || m.key === selection)
}
export function activityTotal(person: BoardPerson, selection: MetricSelection) {
  const measures = selectedMeasures(person, selection)
  const included = measures.filter(m => m.value !== null && !(selection === 'all' && m.overallReason))
  return {
    value: included.length ? included.reduce((sum, m) => sum + m.value!, 0) : null,
    partial: included.length !== measures.length,
    included,
    excluded: measures.filter(m => !included.includes(m)),
  }
}
export function sortPeople(people: BoardPerson[], selection: MetricSelection, order: SortOrder) {
  return [...people].sort((a, b) => {
    const byName = a.name.localeCompare(b.name, 'ja') || a.id.localeCompare(b.id)
    if (order === 'name') return byName
    const av = activityTotal(a, selection).value
    const bv = activityTotal(b, selection).value
    if (av === null) return bv === null ? byName : 1
    if (bv === null) return -1
    return (order === 'asc' ? av - bv : bv - av) || byName
  })
}
export function periodLabel(range: SavedPeriod | null) {
  if (!range) return 'この期間の保存済み集計はありません'
  // periodEnd is exclusive; display the last included calendar date.
  const end = new Date(`${range.periodEnd}T00:00:00Z`)
  end.setUTCDate(end.getUTCDate() - 1)
  return `${range.periodStart.replaceAll('-', '/')} 〜 ${end.toISOString().slice(0, 10).replaceAll('-', '/')}`
}
export function pulseTimestamp(value: string | null | undefined) {
  if (!value || Number.isNaN(Date.parse(value))) return '未確認'
  return new Date(value).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) + ' JST'
}
